const clamp01 = (n) => Math.max(0, Math.min(1, n));
function parseHex(hex) {
    const m = /^#([0-9a-fA-F]{3,8})$/.exec(hex.trim());
    if (!m)
        return null;
    let h = m[1];
    if (h.length === 3 || h.length === 4)
        h = h
            .split("")
            .map((c) => c + c)
            .join("");
    if (h.length === 8)
        h = h.slice(0, 6);
    if (h.length !== 6)
        return null;
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}
function parseRgb(value) {
    const m = /^rgba?\(([^)]+)\)$/i.exec(value.trim());
    if (!m)
        return null;
    const parts = m[1].split(/[,\s/]+/).filter(Boolean);
    if (parts.length < 3)
        return null;
    const channel = (p) => p.endsWith("%") ? (parseFloat(p) / 100) * 255 : parseFloat(p);
    const [r, g, b] = [channel(parts[0]), channel(parts[1]), channel(parts[2])];
    if ([r, g, b].some((n) => !Number.isFinite(n)))
        return null;
    return { r, g, b };
}
function parseHsl(value) {
    const m = /^hsla?\(([^)]+)\)$/i.exec(value.trim());
    if (!m)
        return null;
    const parts = m[1].split(/[,\s/]+/).filter(Boolean);
    if (parts.length < 3)
        return null;
    const h = ((parseFloat(parts[0]) % 360) + 360) % 360;
    const s = clamp01(parseFloat(parts[1]) / 100);
    const l = clamp01(parseFloat(parts[2]) / 100);
    if ([h, s, l].some((n) => !Number.isFinite(n)))
        return null;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const mm = l - c / 2;
    const [r, g, b] = (h < 60
        ? [c, x, 0]
        : h < 120
            ? [x, c, 0]
            : h < 180
                ? [0, c, x]
                : h < 240
                    ? [0, x, c]
                    : h < 300
                        ? [x, 0, c]
                        : [c, 0, x]).map((n) => (n + mm) * 255);
    return { r, g, b };
}
export function parseColor(value) {
    return parseHex(value) ?? parseRgb(value) ?? parseHsl(value);
}
export function isParseable(value) {
    return parseColor(value) != null;
}
const toHex = (n) => Math.max(0, Math.min(255, Math.round(n)))
    .toString(16)
    .padStart(2, "0");
export const rgbToHex = ({ r, g, b }) => `#${toHex(r)}${toHex(g)}${toHex(b)}`;
const srgbToLinear = (c) => c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
const linearToSrgb = (c) => c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
function rgbToOklab({ r, g, b }) {
    const lr = srgbToLinear(r / 255);
    const lg = srgbToLinear(g / 255);
    const lb = srgbToLinear(b / 255);
    const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
    const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
    const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
    return {
        L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
        a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
        b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
    };
}
function oklabToRgb({ L, a, b }) {
    const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
    const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
    const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
    const lr = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
    const lg = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
    const lb = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;
    return {
        r: clamp01(linearToSrgb(lr)) * 255,
        g: clamp01(linearToSrgb(lg)) * 255,
        b: clamp01(linearToSrgb(lb)) * 255,
    };
}
export function oklabMix(color, target, weight) {
    const c = parseColor(color);
    const t = parseColor(target);
    if (!c || !t)
        return color;
    const cl = rgbToOklab(c);
    const tl = rgbToOklab(t);
    const w = clamp01(weight);
    return rgbToHex(oklabToRgb({
        L: cl.L * w + tl.L * (1 - w),
        a: cl.a * w + tl.a * (1 - w),
        b: cl.b * w + tl.b * (1 - w),
    }));
}
export function srgbMix(color, target, weight) {
    const c = parseColor(color);
    const t = parseColor(target);
    if (!c || !t)
        return color;
    const w = clamp01(weight);
    return rgbToHex({
        r: c.r * (1 - w) + t.r * w,
        g: c.g * (1 - w) + t.g * w,
        b: c.b * (1 - w) + t.b * w,
    });
}
export function relativeLuminance(value) {
    const c = parseColor(value);
    if (!c)
        return null;
    const [r, g, b] = [c.r, c.g, c.b].map((n) => srgbToLinear(n / 255));
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    if (la == null || lb == null)
        return null;
    const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
    return (hi + 0.05) / (lo + 0.05);
}
export const WCAG_MIN_RATIO = {
    "AA-normal": 4.5,
    "AA-large": 3,
    "AAA-normal": 7,
};
export function meetsContrast(foreground, background, threshold = "AA-normal") {
    const ratio = contrastRatio(foreground, background);
    return ratio != null && ratio >= WCAG_MIN_RATIO[threshold];
}
export function contrastingText(background) {
    const white = contrastRatio("#ffffff", background);
    const black = contrastRatio("#000000", background);
    if (white == null || black == null)
        return "#ffffff";
    return white >= black ? "#ffffff" : "#000000";
}
export function legibleOn(foreground, background, threshold = "AA-normal") {
    const fg = parseColor(foreground);
    const bg = parseColor(background);
    if (!fg || !bg)
        return foreground;
    const min = WCAG_MIN_RATIO[threshold];
    const bgHex = rgbToHex(bg);
    if (contrastRatio(rgbToHex(fg), bgHex) >= min)
        return foreground;
    const towardWhite = contrastRatio("#ffffff", bgHex) >= contrastRatio("#000000", bgHex);
    const lab = rgbToOklab(fg);
    const STEPS = 50;
    for (let i = 1; i <= STEPS; i++) {
        const t = i / STEPS;
        const L = towardWhite ? lab.L + (1 - lab.L) * t : lab.L * (1 - t);
        const candidate = rgbToHex(oklabToRgb({ L, a: lab.a, b: lab.b }));
        if (contrastRatio(candidate, bgHex) >= min)
            return candidate;
    }
    return towardWhite ? "#ffffff" : "#000000";
}
